import { useEffect, useState, useMemo } from "react";
import { adminApi, formatAud } from "../../lib/api";
import {
  Truck, MapPin, Plus, Trash2, Save, AlertTriangle, CheckCircle2,
  Calculator, TrendingDown, ShieldCheck, Info, Package, DollarSign,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const SHIPPING_STATUSES = ["confirmed", "packed", "shipped", "delivered"];
const SIZE_CATEGORIES   = ["small", "medium", "large", "quote"];

const STATUS_STYLE = {
  confirmed: { background: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" },
  packed:    { background: "#e0e7ff", color: "#3730a3", border: "1px solid #a5b4fc" },
  shipped:   { background: "#ccfbf1", color: "#0f766e", border: "1px solid #5eead4" },
  delivered: { background: "#dcfce7", color: "#15803d", border: "1px solid #86efac" },
};

export default function AdminShipping() {
  const [tab, setTab] = useState("queue");
  return (
    <div className="space-y-5" data-testid="admin-shipping-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color: "#94a3b8" }}>Fulfilment</div>
          <h1 className="font-heading text-[28px] font-bold" style={{ color: "#0f172a" }}>Shipping</h1>
          <p className="text-[12px] mt-0.5" style={{ color: "#64748b" }}>Pack queue · zone rates · margin protection</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1.5 rounded-xl w-fit" style={{ background: "#f5f7fb", border: "1px solid #dde3ef" }}>
        {[["queue", "Pack & Ship"], ["zones", "Margin Calculator"], ["rates", "Part Type Pricing"]].map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`shipping-tab-${t}`}
            className="h-8 px-4 text-[11px] font-mono uppercase tracking-widest rounded-lg transition-all"
            style={tab === t
              ? { background: "#e31e24", color: "#ffffff", fontWeight: 700 }
              : { color: "#64748b" }
            }>
            {label}
          </button>
        ))}
      </div>

      {tab === "queue" ? <ShippingQueue /> : tab === "zones" ? <ShippingZones /> : <PartTypePricing />}
    </div>
  );
}

// ─── Pack & Ship Queue ─────────────────────────────────────────────────
function ShippingQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await adminApi.get("/admin/orders");
    setItems((data.items || []).filter((o) => ["confirmed","packed","shipped"].includes(o.status)));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // ── Status update — original logic untouched ──
  const update = async (o, s) => {
    await adminApi.put(`/admin/orders/${o.id}/status`, { new_status: s });
    toast.success(`Order ${o.order_number} → ${s}`);
    load();
  };

  const statusCounts = { confirmed: 0, packed: 0, shipped: 0 };
  items.forEach((o) => { if (statusCounts[o.status] !== undefined) statusCounts[o.status]++; });

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center shadow-sm" style={{ border: "1px solid #dde3ef" }}>
        <div className="w-6 h-6 border-2 border-[#1e5bb8] border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-14 text-center shadow-sm" style={{ border: "1px solid #dde3ef" }}>
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "#f0fdf4" }}>
          <Truck className="h-8 w-8" style={{ color: "#16a34a" }} />
        </div>
        <div className="font-heading font-bold text-[18px] mb-1" style={{ color: "#0f172a" }}>Queue is empty</div>
        <div className="text-[13px]" style={{ color: "#64748b" }}>All orders are shipped or delivered</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status summary */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { status: "confirmed", label: "To Pack",     color: "#1e40af",  bg: "#dbeafe" },
          { status: "packed",    label: "Ready to Ship",color: "#3730a3", bg: "#e0e7ff" },
          { status: "shipped",   label: "In Transit",   color: "#0f766e", bg: "#ccfbf1" },
        ].map(({ status, label, color, bg }) => (
          <div key={status} className="bg-white rounded-2xl p-4 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color: "#94a3b8" }}>{label}</div>
            <div className="font-heading font-bold text-[26px]" style={{ color }}>{statusCounts[status]}</div>
          </div>
        ))}
      </div>

      {/* Orders */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm" style={{ border: "1px solid #dde3ef" }}>
        {items.map((o, idx) => (
          <div key={o.id}
            className="p-4 flex flex-wrap items-center gap-4 hover:bg-[#f5f7fb] transition-colors"
            style={{ borderBottom: idx < items.length - 1 ? "1px solid #f0f4f8" : "none" }}
            data-testid={`shipping-${o.order_number}`}>

            {/* Order info */}
            <div className="min-w-[140px]">
              <div className="font-heading font-bold text-[14px]" style={{ color: "#0f172a" }}>{o.order_number}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest mt-0.5" style={{ color: "#94a3b8" }}>
                {new Date(o.created_at).toLocaleDateString("en-AU")}
              </div>
            </div>

            {/* Customer + address */}
            <div className="min-w-[200px] flex-1">
              <div className="text-[13px] font-semibold" style={{ color: "#0f172a" }}>{o.customer_name}</div>
              <div className="flex items-center gap-1 text-[11px] mt-0.5" style={{ color: "#64748b" }}>
                <MapPin className="h-3 w-3 shrink-0" />
                {o.shipping_address?.suburb}, {o.shipping_address?.state} {o.shipping_address?.postcode}
              </div>
              {o.customer_phone && (
                <div className="text-[11px] font-mono mt-0.5" style={{ color: "#94a3b8" }}>{o.customer_phone}</div>
              )}
            </div>

            {/* Items + value */}
            <div className="text-center">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
                style={{ background: "#f5f7fb", border: "1px solid #dde3ef" }}>
                <Package className="h-3.5 w-3.5" style={{ color: "#64748b" }} />
                <span className="text-[12px] font-medium" style={{ color: "#0f172a" }}>
                  {o.items?.length || 0} items
                </span>
                <span className="text-[12px] font-mono font-bold" style={{ color: "#16a34a" }}>
                  · {formatAud(o.total)}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              {/* Status select */}
              <select
                value={o.status}
                onChange={(e) => update(o, e.target.value)}
                className="h-9 px-3 pr-7 rounded-xl text-[11px] font-mono font-bold uppercase tracking-wide cursor-pointer outline-none"
                style={{
                  ...(STATUS_STYLE[o.status] || { background: "#f5f7fb", color: "#475569", border: "1px solid #dde3ef" }),
                  WebkitAppearance: "none", appearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center",
                }}>
                {SHIPPING_STATUSES.map((s) => (
                  <option key={s} value={s} style={{ background: "#ffffff", color: "#0f172a" }}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>

              <Button size="sm" onClick={() => update(o, "shipped")}
                className="rounded-xl font-semibold text-[12px]"
                style={{ background: "#16a34a", color: "#ffffff" }}
                disabled={o.status === "shipped" || o.status === "delivered"}>
                <Truck className="h-3.5 w-3.5 mr-1" /> Mark Shipped
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Shipping Zones with margin-protection math ───────────────────────
const EMPTY_ZONE = { name: "", states: "", rate: 0, size_category: "medium", free_above: "", carrier_cost: "" };

function ShippingZones() {
  const [zones,   setZones]   = useState([]);
  const [form,    setForm]    = useState(EMPTY_ZONE);
  const [editing, setEditing] = useState(null);
  // Rate preview calculator
  const [previewTotal, setPreviewTotal] = useState(150);

  const load = async () => {
    const { data } = await adminApi.get("/admin/shipping-zones");
    setZones(data.items || []);
  };
  useEffect(() => { load(); }, []);

  // ── All zone CRUD logic untouched ────────────────────────────────
  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name:          form.name,
      states:        form.states.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
      rate:          Number(form.rate || 0),
      size_category: form.size_category,
      free_above:    form.free_above === "" ? null : Number(form.free_above),
    };
    if (editing) {
      await adminApi.put(`/admin/shipping-zones/${editing}`, payload);
      toast.success("Zone updated");
    } else {
      await adminApi.post("/admin/shipping-zones", payload);
      toast.success("Zone created");
    }
    setForm(EMPTY_ZONE); setEditing(null); load();
  };

  const edit = (z) => {
    setEditing(z.id);
    setForm({
      name: z.name, states: (z.states || []).join(","), rate: z.rate,
      size_category: z.size_category, free_above: z.free_above ?? "",
      carrier_cost: z.carrier_cost ?? "",
    });
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this zone?")) return;
    await adminApi.delete(`/admin/shipping-zones/${id}`);
    toast.success("Zone deleted");
    load();
  };
  // ── End logic ────────────────────────────────────────────────────

  // ── Margin protection math (frontend only, no backend needed) ────
  const analyseZone = (z) => {
    const carrierCost    = Number(z.carrier_cost || 0);
    const customerRate   = Number(z.rate || 0);
    const freeAbove      = z.free_above ? Number(z.free_above) : null;
    const profitPerShipment = customerRate - carrierCost;

    return {
      carrierCost,
      customerRate,
      freeAbove,
      profitPerShipment,
      // Loss risk: when free_above kicks in, you absorb carrier cost
      lossPerFreeOrder: freeAbove ? carrierCost : 0,
      // Break-even: order value where margin covers shipping cost at 30% product margin
      // i.e., 0.3 × orderValue ≥ carrierCost → orderValue ≥ carrierCost / 0.3
      breakEvenAt30: carrierCost > 0 ? Math.ceil(carrierCost / 0.30) : null,
      // At preview total, what does customer pay?
      previewCharge: (freeAbove && previewTotal >= freeAbove) ? 0 : customerRate,
      // Danger: free threshold is below break-even
      isDangerous: freeAbove && carrierCost > 0 && freeAbove < (carrierCost / 0.30),
    };
  };

  const inp = { background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" };

  return (
    <div className="space-y-5">
      {/* ── MARGIN PROTECTION GUIDE ─────────────────────────────────── */}
      <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#fffbeb" }}>
            <ShieldCheck className="h-4 w-4" style={{ color: "#d97706" }} />
          </div>
          <div>
            <div className="font-heading font-bold text-[15px]" style={{ color: "#0f172a" }}>Margin Protection Guide</div>
            <div className="text-[11px]" style={{ color: "#94a3b8" }}>How to set zones so you never lose money on shipping</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              icon: DollarSign, color: "#1e5bb8", bg: "#eff6ff",
              title: "Rate vs Carrier Cost",
              body: "Set 'Carrier Cost' to what you actually pay (e.g. $12.95 for Sendle QLD). Your rate to customer should be ≥ this to break even per shipment.",
            },
            {
              icon: TrendingDown, color: "#e31e24", bg: "#fef2f2",
              title: "Free Shipping Threshold",
              body: "Every free-shipping order costs you the carrier fee. Safe threshold = carrier cost ÷ 0.30 (assumes 30% product margin). E.g. $12.95 ÷ 0.30 = $43 minimum.",
            },
            {
              icon: Calculator, color: "#16a34a", bg: "#f0fdf4",
              title: "Carrier Cost field",
              body: "Fill in 'Carrier Cost Estimate' per zone. The system shows your real profit per shipment and flags zones where free shipping causes losses.",
            },
          ].map(({ icon: Icon, color, bg, title, body }) => (
            <div key={title} className="rounded-xl p-3 flex gap-3" style={{ background: bg, border: `1px solid ${bg}` }}>
              <Icon className="h-5 w-5 shrink-0 mt-0.5" style={{ color }} />
              <div>
                <div className="font-semibold text-[13px] mb-1" style={{ color: "#0f172a" }}>{title}</div>
                <div className="text-[12px] leading-relaxed" style={{ color: "#475569" }}>{body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── RATE PREVIEW CALCULATOR ─────────────────────────────────── */}
      <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
        <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5" style={{ color: "#1e5bb8" }} />
            <div className="font-heading font-bold text-[15px]" style={{ color: "#0f172a" }}>Rate Preview Calculator</div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[12px] font-medium" style={{ color: "#64748b" }}>Order total: </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono font-bold text-[13px]" style={{ color: "#94a3b8" }}>$</span>
              <input type="number" value={previewTotal} onChange={(e) => setPreviewTotal(Number(e.target.value))}
                className="w-28 h-9 pl-7 pr-3 rounded-xl text-[13px] font-mono font-bold outline-none"
                style={{ ...inp, border: "1px solid #dde3ef" }} />
            </div>
          </div>
        </div>

        {zones.length === 0 ? (
          <div className="text-center py-4 text-[12px] font-mono" style={{ color: "#94a3b8" }}>Add zones below to see preview</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {zones.map((z) => {
              const a = analyseZone(z);
              return (
                <div key={z.id} className="rounded-xl p-3 text-center"
                  style={{ background: a.isDangerous ? "#fef2f2" : "#f5f7fb", border: `1px solid ${a.isDangerous ? "#fca5a5" : "#dde3ef"}` }}>
                  <div className="text-[11px] font-mono uppercase tracking-widest mb-1.5 truncate" style={{ color: "#64748b" }}>
                    {z.name}
                  </div>
                  <div className="font-heading font-bold text-[22px]"
                    style={{ color: a.previewCharge === 0 ? "#16a34a" : "#0f172a" }}>
                    {a.previewCharge === 0 ? "FREE" : formatAud(a.previewCharge)}
                  </div>
                  {a.previewCharge === 0 && a.carrierCost > 0 && (
                    <div className="text-[10px] font-mono mt-1" style={{ color: "#e31e24" }}>
                      ⚠ Costs you {formatAud(a.carrierCost)}
                    </div>
                  )}
                  {a.previewCharge > 0 && a.carrierCost > 0 && (
                    <div className="text-[10px] font-mono mt-1" style={{ color: a.profitPerShipment >= 0 ? "#16a34a" : "#e31e24" }}>
                      {a.profitPerShipment >= 0 ? `+${formatAud(a.profitPerShipment)} margin` : `${formatAud(a.profitPerShipment)} loss`}
                    </div>
                  )}
                  {a.freeAbove && (
                    <div className="text-[9px] font-mono mt-1" style={{ color: "#94a3b8" }}>
                      Free ≥ {formatAud(a.freeAbove)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── ZONES TABLE + FORM ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Zone table */}
        <div className="lg:col-span-2 bg-white rounded-2xl overflow-hidden shadow-sm overflow-x-auto" style={{ border: "1px solid #dde3ef" }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "#f5f7fb", borderBottom: "1px solid #dde3ef" }}>
                {["Zone", "States", "Size", "Customer Rate", "Free Above", "Carrier Cost", "Risk", ""].map((h, i) => (
                  <th key={h} className="py-3 px-4 font-mono font-semibold uppercase"
                    style={{ color: "#475569", fontSize: 9, letterSpacing: "0.12em", textAlign: i >= 3 ? "right" : "left" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {zones.map((z, idx) => {
                const a = analyseZone(z);
                return (
                  <tr key={z.id} className="hover:bg-[#f5f7fb] transition-colors"
                    style={{ borderBottom: idx < zones.length - 1 ? "1px solid #f0f4f8" : "none" }}
                    data-testid={`zone-row-${z.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-heading font-bold text-[14px]" style={{ color: "#0f172a" }}>{z.name}</div>
                    </td>
                    <td className="px-4 py-3 text-[11px] font-mono" style={{ color: "#64748b" }}>
                      {(z.states || []).join(", ") || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="badge badge-gray">{z.size_category}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-bold" style={{ color: "#0f172a" }}>
                      {formatAud(z.rate)}
                    </td>
                    <td className="px-4 py-3 text-right font-mono" style={{ color: "#64748b" }}>
                      {z.free_above ? formatAud(z.free_above) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-[12px]" style={{ color: a.carrierCost > 0 ? "#0f172a" : "#94a3b8" }}>
                      {a.carrierCost > 0 ? formatAud(a.carrierCost) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {a.isDangerous ? (
                        <div title={`Free threshold $${z.free_above} is below break-even $${a.breakEvenAt30}`}>
                          <span className="badge badge-red">
                            <AlertTriangle className="h-2.5 w-2.5" /> Loss risk
                          </span>
                        </div>
                      ) : a.carrierCost > 0 ? (
                        <span className="badge badge-green">
                          <CheckCircle2 className="h-2.5 w-2.5" /> Safe
                        </span>
                      ) : (
                        <span className="badge badge-gray">
                          <Info className="h-2.5 w-2.5" /> No cost set
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" onClick={() => edit(z)}
                          className="rounded-lg text-[11px]"
                          style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}
                          data-testid={`zone-edit-${z.id}`}>
                          Edit
                        </Button>
                        <Button size="sm" onClick={() => remove(z.id)}
                          className="rounded-lg text-[11px]"
                          style={{ background: "#fef2f2", color: "#e31e24", border: "1px solid #fca5a5" }}
                          data-testid={`zone-delete-${z.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {zones.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-10 text-center text-[12px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>
                    No zones configured
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Zone form */}
        <form onSubmit={save} className="bg-white rounded-2xl p-5 shadow-sm space-y-3" style={{ border: "1px solid #dde3ef" }} data-testid="zone-form">
          <div className="font-heading font-bold text-[15px]" style={{ color: "#0f172a" }}>
            {editing ? "Edit zone" : "Add new zone"}
          </div>

          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Zone name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 rounded-xl" style={inp} required placeholder="e.g. QLD Metro"
              data-testid="zone-name-input" />
          </div>

          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>States (comma sep.)</Label>
            <Input value={form.states} onChange={(e) => setForm({ ...form, states: e.target.value })}
              className="mt-1 rounded-xl" style={inp} placeholder="QLD,NSW,VIC" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>
                Customer Rate (AUD)
              </Label>
              <Input type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })}
                className="mt-1 rounded-xl" style={inp} data-testid="zone-rate-input" />
            </div>
            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Size category</Label>
              <Select value={form.size_category} onValueChange={(v) => setForm({ ...form, size_category: v })}>
                <SelectTrigger className="mt-1 rounded-xl" style={inp}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SIZE_CATEGORIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>
              Free above (AUD) — optional
            </Label>
            <Input type="number" step="0.01" value={form.free_above}
              onChange={(e) => setForm({ ...form, free_above: e.target.value })}
              className="mt-1 rounded-xl" style={inp} placeholder="e.g. 350"
              data-testid="zone-free-input" />
            <div className="text-[10px] font-mono mt-1" style={{ color: "#94a3b8" }}>
              Orders above this total get free shipping
            </div>
          </div>

          {/* CARRIER COST — new field for margin protection */}
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>
              Carrier cost estimate (AUD)
              <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-md" style={{ background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a" }}>
                margin protection
              </span>
            </Label>
            <Input type="number" step="0.01" value={form.carrier_cost}
              onChange={(e) => setForm({ ...form, carrier_cost: e.target.value })}
              className="mt-1 rounded-xl" style={inp}
              placeholder="e.g. 12.95 (what you pay Sendle/AusPost)" />
            <div className="text-[10px] font-mono mt-1" style={{ color: "#94a3b8" }}>
              What you actually pay the courier per parcel
            </div>
          </div>

          {/* Live margin preview for form */}
          {(form.rate > 0 || form.carrier_cost > 0) && (
            <div className="rounded-xl p-3 space-y-1.5" style={{ background: "#f5f7fb", border: "1px solid #dde3ef" }}>
              <div className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: "#94a3b8" }}>
                Live margin preview
              </div>
              {form.carrier_cost > 0 && form.rate > 0 && (
                <div className="flex items-center justify-between text-[12px]">
                  <span style={{ color: "#64748b" }}>Profit per paid shipment</span>
                  <span className="font-mono font-bold"
                    style={{ color: (form.rate - form.carrier_cost) >= 0 ? "#16a34a" : "#e31e24" }}>
                    {(form.rate - form.carrier_cost) >= 0 ? "+" : ""}{formatAud(form.rate - form.carrier_cost)}
                  </span>
                </div>
              )}
              {form.free_above && form.carrier_cost > 0 && (
                <div className="flex items-center justify-between text-[12px]">
                  <span style={{ color: "#64748b" }}>Loss per free-shipping order</span>
                  <span className="font-mono font-bold" style={{ color: "#e31e24" }}>
                    −{formatAud(form.carrier_cost)}
                  </span>
                </div>
              )}
              {form.carrier_cost > 0 && (
                <div className="flex items-center justify-between text-[12px]">
                  <span style={{ color: "#64748b" }}>Safe free threshold (at 30% margin)</span>
                  <span className="font-mono font-bold" style={{ color: "#0f172a" }}>
                    {formatAud(Math.ceil(form.carrier_cost / 0.30))}
                  </span>
                </div>
              )}
              {form.free_above && form.carrier_cost > 0 && Number(form.free_above) < Math.ceil(form.carrier_cost / 0.30) && (
                <div className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 mt-1"
                  style={{ background: "#fee2e2", border: "1px solid #fca5a5" }}>
                  <AlertTriangle className="h-3 w-3 shrink-0" style={{ color: "#e31e24" }} />
                  <span className="text-[11px]" style={{ color: "#991b1b" }}>
                    Free threshold is too low — raise to {formatAud(Math.ceil(form.carrier_cost / 0.30))} to avoid losses
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button type="submit"
              className="flex-1 text-white font-bold uppercase tracking-wide rounded-xl text-[13px]"
              style={{ background: "#e31e24" }}
              data-testid="zone-save-btn">
              {editing ? <><Save className="h-4 w-4 mr-2" /> Update</> : <><Plus className="h-4 w-4 mr-2" /> Add zone</>}
            </Button>
            {editing && (
              <Button type="button" onClick={() => { setEditing(null); setForm(EMPTY_ZONE); }}
                className="rounded-xl"
                style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Single collapsed row for a part type group — expand to edit ───────────
function PartTypeGroupRow({ group, isLast, onUpdate, onRemove, onToggleCategory, availableCategories, mappedCategories, inp }) {
  const [editing, setEditing] = useState(false);
  const catCount = (group.categories || []).length;

  if (!editing) {
    const allRates = ZONE_KEYS.map(z => Number(group[z] || 0));
    const minRate = Math.min(...allRates);
    const maxRate = Math.max(...allRates);
    return (
      <div className="flex items-center gap-4 px-5 py-3.5"
        style={{ borderBottom: isLast ? "none" : "1px solid #f0f4f8" }}>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[13px] truncate" style={{ color: "#0f172a" }}>{group.label || "Untitled group"}</div>
          <div className="text-[11px] font-mono" style={{ color: "#94a3b8" }}>
            {catCount} categor{catCount === 1 ? "y" : "ies"} mapped
          </div>
        </div>
        <div className="text-center shrink-0">
          <div style={{ color: "#94a3b8", fontSize: 9, textTransform: "uppercase" }}>Rate range</div>
          <div className="font-bold font-mono text-[13px]" style={{ color: "#0f172a" }}>
            ${minRate.toFixed(2)} – ${maxRate.toFixed(2)}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" onClick={() => setEditing(true)}
            className="rounded-lg text-[11px]" style={{ background: "#f5f7fb", border: "1px solid #dde3ef", color: "#475569" }}>
            Edit
          </Button>
          <Button size="sm" onClick={onRemove}
            className="rounded-lg" style={{ background: "#fef2f2", color: "#e31e24", border: "1px solid #fca5a5" }}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5" style={{ background: "#f8fafc", borderBottom: isLast ? "none" : "1px solid #f0f4f8" }}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <Input value={group.label} onChange={(e) => onUpdate({ label: e.target.value })}
          className="rounded-xl font-bold max-w-sm bg-white" style={inp} placeholder="e.g. Mirrors, Lights & Small Electrical" />
        <Button size="sm" onClick={() => setEditing(false)}
          className="rounded-lg shrink-0" style={{ background: "#0b1a3b", color: "#fff" }}>
          Done
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        {ZONE_KEYS.map(z => (
          <div key={z}>
            <Label className="text-[10px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>{ZONE_LABELS[z]}</Label>
            <Input type="number" step="0.01" value={group[z]}
              onChange={(e) => onUpdate({ [z]: e.target.value })}
              className="mt-1 rounded-xl bg-white" style={inp} />
          </div>
        ))}
      </div>

      <div>
        <Label className="text-[10px] font-mono uppercase tracking-wide mb-2 block" style={{ color: "#64748b" }}>
          Part categories in this group ({catCount} selected)
        </Label>
        <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-2 rounded-xl bg-white" style={{ border: "1px solid #eef2f8" }}>
          {availableCategories.map(c => {
            const selected = (group.categories || []).includes(c.name);
            const usedElsewhere = !selected && mappedCategories.has(c.name);
            return (
              <button key={c.name} disabled={usedElsewhere}
                onClick={() => onToggleCategory(c.name)}
                title={usedElsewhere ? "Already assigned to another group" : ""}
                className="px-2.5 py-1 rounded-lg text-[11px] font-mono transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                style={selected
                  ? { background: "#0b1a3b", color: "#fff" }
                  : { background: "#fff", color: "#64748b", border: "1px solid #dde3ef" }}>
                {c.name} <span className="opacity-60">({c.count})</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
const ZONE_KEYS = ["metro_near", "metro_far", "regional_near", "regional_far", "rural_near", "rural_far"];
const ZONE_LABELS = {
  metro_near: "Metro · Near", metro_far: "Metro · Far",
  regional_near: "Regional · Near", regional_far: "Regional · Far",
  rural_near: "Rural · Near", rural_far: "Rural · Far",
};
const ZONE_KEYS_SHORT = { metro_near: "Metro N", metro_far: "Metro F", regional_near: "Reg. N", regional_far: "Reg. F", rural_near: "Rural N", rural_far: "Rural F" };
const ZONE_TYPES = ["metro", "regional", "rural"];
const ZONE_TYPE_LABELS = { metro: "Metro", regional: "Regional", rural: "Rural" };

function PartTypePricing() {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("flat");
  const [flatRate, setFlatRate] = useState(15);
  const [freeThreshold, setFreeThreshold] = useState(250);
  const [groups, setGroups] = useState([]);
  const [defaultGroup, setDefaultGroup] = useState({
    metro_near: 12, metro_far: 18, regional_near: 22, regional_far: 30, rural_near: 35, rural_far: 50,
  });
  const [zoneDistance, setZoneDistance] = useState({}); // { QLD: {metro:"near", regional:"near", rural:"near"}, ... }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await adminApi.get("/admin/settings/shipping");
    setMode(data.shipping_mode);
    setFlatRate(data.shipping_flat);
    setFreeThreshold(data.free_shipping_threshold);
    setGroups(data.shipping_groups || []);
    setDefaultGroup(data.shipping_default_group || {
      metro_near: 12, metro_far: 18, regional_near: 22, regional_far: 30, rural_near: 35, rural_far: 50,
    });
    setZoneDistance(data.shipping_zone_distance || {});
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const toggleZoneDistance = (state, zoneType) => {
    setZoneDistance(prev => {
      const current = prev[state]?.[zoneType] || "near";
      const next = current === "near" ? "far" : "near";
      return { ...prev, [state]: { ...prev[state], [zoneType]: next } };
    });
  };

  const mappedCategories = new Set(groups.flatMap(g => g.categories || []));
  const unmappedCategories = (data?.available_categories || []).filter(c => !mappedCategories.has(c.name));

  const addGroup = () => {
    setGroups([...groups, {
      id: `group_${Date.now()}`, label: "New Part Type Group",
      categories: [],
      metro_near: 12, metro_far: 18, regional_near: 22, regional_far: 30, rural_near: 35, rural_far: 50,
    }]);
  };
  const removeGroup = (id) => setGroups(groups.filter(g => g.id !== id));
  const updateGroup = (id, patch) => setGroups(groups.map(g => g.id === id ? { ...g, ...patch } : g));
  const toggleCategoryInGroup = (groupId, catName) => {
    setGroups(groups.map(g => {
      if (g.id !== groupId) return g;
      const has = (g.categories || []).includes(catName);
      return { ...g, categories: has ? g.categories.filter(c => c !== catName) : [...g.categories, catName] };
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put("/admin/settings/shipping", {
        shipping_mode: mode,
        shipping_flat: Number(flatRate),
        free_shipping_threshold: Number(freeThreshold),
        shipping_groups: groups.map(g => ({
          ...g,
          metro_near: Number(g.metro_near), metro_far: Number(g.metro_far),
          regional_near: Number(g.regional_near), regional_far: Number(g.regional_far),
          rural_near: Number(g.rural_near), rural_far: Number(g.rural_far),
        })),
        shipping_default_group: Object.fromEntries(
          ZONE_KEYS.map(z => [z, Number(defaultGroup[z])])
        ),
        shipping_zone_distance: zoneDistance,
      });
      toast.success("Shipping pricing saved — live on checkout immediately");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const inp = { background: "#ffffff", borderColor: "#dde3ef", color: "#0f172a" };

  if (!data) {
    return (
      <div className="bg-white rounded-2xl p-12 text-center shadow-sm" style={{ border: "1px solid #dde3ef" }}>
        <div className="w-6 h-6 border-2 border-[#1e5bb8] border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* Explainer */}
      <div className="rounded-2xl p-5" style={{ background: "#eff6ff", border: "1px solid #93c5fd" }}>
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#1e40af" }} />
          <div>
            <div className="font-heading font-bold text-[14px] mb-1" style={{ color: "#1e3a8a" }}>
              This is the system that actually calculates checkout shipping
            </div>
            <div className="text-[12px] leading-relaxed" style={{ color: "#1e40af" }}>
              Group your 30,000+ parts by type (mirrors, doors, engines, etc — using categories already synced from Pinnacle),
              set a price for Metro / Regional / Rural delivery per group. When a customer checks out, the system finds the
              highest-priced part in their cart, matches it to a group, and charges the rate for their delivery postcode zone.
              No manual pricing per part needed — ever.
            </div>
          </div>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
        <div className="font-heading font-bold text-[15px] mb-3" style={{ color: "#0f172a" }}>Shipping Mode</div>
        <div className="flex gap-3">
          <button onClick={() => setMode("flat")}
            className="flex-1 rounded-xl p-4 text-left transition-all"
            style={mode === "flat"
              ? { background: "#0b1a3b", color: "#fff", border: "2px solid #0b1a3b" }
              : { background: "#f5f7fb", color: "#475569", border: "2px solid #dde3ef" }}>
            <div className="font-bold text-[13px] mb-1">Flat Rate (current default)</div>
            <div className="text-[11px] opacity-80">Same shipping price for every part, regardless of type or destination.</div>
          </button>
          <button onClick={() => setMode("zone_category")}
            className="flex-1 rounded-xl p-4 text-left transition-all"
            style={mode === "zone_category"
              ? { background: "#0b1a3b", color: "#fff", border: "2px solid #0b1a3b" }
              : { background: "#f5f7fb", color: "#475569", border: "2px solid #dde3ef" }}>
            <div className="font-bold text-[13px] mb-1">Part Type + Zone (recommended)</div>
            <div className="text-[11px] opacity-80">Price varies by part type and delivery zone — more accurate freight cost.</div>
          </button>
        </div>
      </div>

      {/* Flat rate settings — only shown in flat mode */}
      {mode === "flat" && (
        <div className="bg-white rounded-2xl p-5 shadow-sm grid grid-cols-2 gap-4" style={{ border: "1px solid #dde3ef" }}>
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Flat shipping rate (AUD)</Label>
            <Input type="number" step="0.01" value={flatRate} onChange={(e) => setFlatRate(e.target.value)}
              className="mt-1 rounded-xl" style={inp} />
          </div>
          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Free shipping above (AUD)</Label>
            <Input type="number" step="0.01" value={freeThreshold} onChange={(e) => setFreeThreshold(e.target.value)}
              className="mt-1 rounded-xl" style={inp} />
          </div>
        </div>
      )}

      {/* Zone+category settings — only shown in zone_category mode */}
      {mode === "zone_category" && (
        <>
          <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>Free shipping above (AUD) — applies across all groups</Label>
            <Input type="number" step="0.01" value={freeThreshold} onChange={(e) => setFreeThreshold(e.target.value)}
              className="mt-1 rounded-xl max-w-xs" style={inp} />
          </div>

          {/* Zone Mapping — one-time per-state setup, independent of part type groups */}
          <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <div className="font-heading font-bold text-[14px] mb-1" style={{ color: "#0f172a" }}>Zone Mapping</div>
            <div className="text-[11px] mb-4" style={{ color: "#94a3b8" }}>
              For each state, mark whether Metro / Regional / Rural delivery there counts as "Near" (cheaper, closer
              freight lane) or "Far" (further, pricier). Set once — every part type group below then uses these 6
              combined tiers automatically. Click a cell to toggle.
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr>
                    <th className="text-left pb-2 font-mono uppercase text-[10px]" style={{ color: "#94a3b8" }}>State</th>
                    {ZONE_TYPES.map(zt => (
                      <th key={zt} className="text-center pb-2 font-mono uppercase text-[10px]" style={{ color: "#94a3b8" }}>{ZONE_TYPE_LABELS[zt]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data?.states || []).map(state => (
                    <tr key={state} style={{ borderTop: "1px solid #f0f4f8" }}>
                      <td className="py-2 font-bold" style={{ color: "#0f172a" }}>{state}</td>
                      {ZONE_TYPES.map(zt => {
                        const val = zoneDistance[state]?.[zt] || "near";
                        const isFar = val === "far";
                        return (
                          <td key={zt} className="py-2 text-center">
                            <button onClick={() => toggleZoneDistance(state, zt)}
                              className="px-3 py-1 rounded-full text-[10px] font-mono font-bold uppercase tracking-wide transition-all"
                              style={isFar
                                ? { background: "#fef2f2", color: "#e31e24", border: "1px solid #fca5a5" }
                                : { background: "#f0fdf4", color: "#16a34a", border: "1px solid #86efac" }}>
                              {isFar ? "Far" : "Near"}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Default group — for any unmapped category */}
          <div className="bg-white rounded-2xl p-5 shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            <div className="font-heading font-bold text-[14px] mb-1" style={{ color: "#0f172a" }}>Default Rate</div>
            <div className="text-[11px] mb-3" style={{ color: "#94a3b8" }}>
              Used for any part category not yet assigned to a group below ({unmappedCategories.length} unmapped categories)
            </div>
            <div className="grid grid-cols-3 gap-3">
              {ZONE_KEYS.map(z => (
                <div key={z}>
                  <Label className="text-[10px] font-mono uppercase tracking-wide" style={{ color: "#64748b" }}>{ZONE_LABELS[z]}</Label>
                  <Input type="number" step="0.01" value={defaultGroup[z]}
                    onChange={(e) => setDefaultGroup({ ...defaultGroup, [z]: e.target.value })}
                    className="mt-1 rounded-xl" style={inp} />
                </div>
              ))}
            </div>
          </div>

          {/* Part type groups — collapsed rows, expand to edit */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-sm" style={{ border: "1px solid #dde3ef" }}>
            {groups.map((g, idx) => (
              <PartTypeGroupRow
                key={g.id}
                group={g}
                isLast={idx === groups.length - 1}
                onUpdate={(patch) => updateGroup(g.id, patch)}
                onRemove={() => removeGroup(g.id)}
                onToggleCategory={(cat) => toggleCategoryInGroup(g.id, cat)}
                availableCategories={data.available_categories || []}
                mappedCategories={mappedCategories}
                inp={inp}
              />
            ))}
            {groups.length === 0 && (
              <div className="p-10 text-center text-[12px] font-mono uppercase tracking-widest" style={{ color: "#94a3b8" }}>
                No part type groups yet — add one below
              </div>
            )}
          </div>

          <button onClick={addGroup}
            className="w-full rounded-2xl p-4 flex items-center justify-center gap-2 text-[13px] font-bold transition-all"
            style={{ background: "#f5f7fb", border: "2px dashed #dde3ef", color: "#64748b" }}>
            <Plus className="h-4 w-4" /> Add Part Type Group
          </button>

          {unmappedCategories.length > 0 && (
            <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "#d97706" }} />
              <div className="text-[12px]" style={{ color: "#92400e" }}>
                <strong>{unmappedCategories.length} categories</strong> aren't assigned to a group yet and will use the Default Rate above:{" "}
                {unmappedCategories.slice(0, 8).map(c => c.name).join(", ")}
                {unmappedCategories.length > 8 && ` +${unmappedCategories.length - 8} more`}
              </div>
            </div>
          )}
        </>
      )}

      <Button onClick={save} disabled={saving}
        className="w-full h-12 text-white font-bold uppercase tracking-wide rounded-xl text-[14px]"
        style={{ background: "#e31e24" }}>
        {saving ? "Saving…" : <><Save className="h-4 w-4 mr-2" /> Save Shipping Pricing</>}
      </Button>
    </div>
  );
}